import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Badge, Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../../../../components/common/Flex';
import { PieChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import BasicECharts from '../../../../../components/common/BasicEChart';
import {
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../../providers/AppProvider';
import { segundosATiempo } from '../../../../../helpers/utils';
import { formatearTiempo, obtenerTiempoMotor } from '../../../../../utils/useReporteCalculations';
import FalconCardHeader from '../../../../../components/common/FalconCardHeader';
import ReactEchart from '../../../../../components/common/ReactEchart';
import { Title } from 'chart.js';

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    PieChart,
    CanvasRenderer,
    LegendComponent
]);

// ✅ PROCESAR DATOS CON ORDEN INVERTIDO (A/E) Y FILTRADO
const processData = (resumenUnidad) => {
    console.log("[EstadiasTotal processData] Resumen recibido:", resumenUnidad);
    
    if (!resumenUnidad) {
        return [];  // ✅ Retornar array vacío si no hay resumen
    }

    // ✅ ORDEN INVERTIDO: APAGADO PRIMERO, ENCENDIDO SEGUNDO
    const allData = [
        { 
            name: 'Motor Apagado',     // ✅ PRIMERO: Apagado
            color: '#dc3545',         // ✅ Rojo para Apagado
            segundos: resumenUnidad.tiempo_total_apagado || 0
        },
        { 
            name: 'Motor Encendido',   // ✅ SEGUNDO: Encendido  
            color: '#28a745',         // ✅ Verde para Encendido
            segundos: resumenUnidad.tiempo_total_encendido || 0
        }
    ];

    // ✅ FILTRAR SOLO LOS ELEMENTOS CON SEGUNDOS > 0
    const groupedData = allData.filter(item => item.segundos > 0);

    console.log("[EstadiasTotal processData] Datos procesados (A/E):", groupedData);
    console.log("[EstadiasTotal processData] Datos filtrados (solo > 0):", groupedData.length, "de", allData.length);
    
    return groupedData;
};

const getOptionsPie = (data, getThemeColor) => {
    if (!getThemeColor || typeof getThemeColor !== 'function') {
        console.error("getThemeColor is not a function or is undefined");
        return {};
    }

    return {
        tooltip: {
            trigger: 'item',
            padding: [7, 10],
            backgroundColor: '#fff',
            borderColor: '#ccc',
            textStyle: { color: '#000' },
            borderWidth: 1,
            transitionDuration: 0,
            formatter: function (params) {
                return `<strong>${params.data.name}:</strong> ${params.data.percentage.toFixed(0)}%`;
            }
        },
        legend: { show: false },
        series: [
            {
                name: 'A/E',
                type: 'pie',
                radius: ['100%', '67%'],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderWidth: 2,
                    borderColor: '#fff'
                },
                label: {
                    show: false,
                    formatter: '{b}: {d}%'
                },
                emphasis: {
                    scale: false
                },
                labelLine: { show: false },
                data: data.map(group => {
                    return ({ value: group.segundos, name: group.name, itemStyle: { color: group.color }, groupTitle: group.name, alertType: group.name,percentage:((group.segundos/group.totalHours)*100) });
                })
            }
        ]
    };
};
// ✅ CONFIGURACIÓN DE ECHARTS CON ETIQUETA A/E
const getOptions = (getThemeColor, data, radius) => {
    const totalSegundos = data.reduce((acc, item) => acc + item.segundos, 0);
    
    console.log("[EstadiasTotal getOptions] Total segundos A/E:", totalSegundos);
    console.log("[EstadiasTotal getOptions] Datos después del filtrado:", data.length);
    
    // Si no hay datos después del filtrado, mostrar gráfico vacío
    if (data.length === 0 || totalSegundos === 0) {
        return {
            series: [{
                name: 'A/E',  // ✅ Cambio de E/A a A/E
                type: 'pie',
                radius,
                data: [{ value: 1, name: 'Sin datos', itemStyle: { color: '#e9ecef' } }],
                label: {
                    show: true,
                    position: 'center',
                    formatter: 'Sin\ndatos',
                    fontSize: 16,
                    color: getThemeColor('gray-600')
                }
            }]
        };
    }

    return {
        color: data.map(d => d.color),
        tooltip: {
            padding: [7, 10],
            transitionDuration: 0,
            backgroundColor: getThemeColor('gray-100'),
            borderColor: getThemeColor('gray-300'),
            textStyle: { color: getThemeColor('gray-1100') },
            borderWidth: 1,
            formatter: params => {
                const porcentaje = totalSegundos > 0 ? ((params.value / totalSegundos) * 100).toFixed(1) : 0;
                const tiempo = formatearTiempo(params.value);
                return `<strong>${params.data.name}:</strong><br/>${tiempo} hr(s) (${porcentaje}%)`;
            }
        },
        series: [{
            name: 'A/E',  // ✅ Cambio de E/A a A/E
            type: 'pie',
            radius,
            avoidLabelOverlap: false,
            emphasis: {
                scale: false
            },
            itemStyle: {
                borderWidth: 2,
                borderColor: getThemeColor('gray-100')
            },
            label: {
                show: true,
                position: 'center',
                formatter: '{a}',  // Mostrará "A/E"
                fontSize: 23,
                color: getThemeColor('gray-1100')
            },
            labelLine: {
                show: false
            },
            data: data.map(d => ({
                value: d.segundos,
                name: d.name,
                itemStyle: { color: d.color }
            }))
        }]
    };
};

const EstadiasTotalItem = ({ item, index, total,totalItems }) => {
    const { name, color, segundos } = item;
    
    // Calcular porcentaje de manera segura
    const percentage = total > 0 ? ((segundos * 100) / total) : 0;
    
    // Formatear tiempo usando la función genérica
    const tiempoFormateado = formatearTiempo(segundos);

    const style = {
        display: "-webkit-box",
        WebkitLineClamp: 1, // Limita a 2 líneas
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "normal",
    };
    
    let isLast = index === totalItems - 1;
    let highlighted = false;
    return (
        <div className=' pe-4'>
            <Row
                className={classNames(
                    'position-relative align-items-center py-2 g-0 flex-nowrap',
                    { 'border-bottom border-200': !isLast },
                    { 'text-danger fw-bold': highlighted }
                )} style={{ minWidth: '280px' }} href="#!" 
            >
                    <FontAwesomeIcon
                    icon="circle"
                     size="xs"
                     className='me-3'
                    style={{ color: color}}
                    
                />
                <Col className="py-1">
                    <Flex className="justify-content-between" >
                        <Row className='mx-0 px-0' style={{ width: '50%' }}>
                            <Flex className={classNames(
                                'm-0 p-0 align-items-center text-truncate flex-grow-1 flex-nowrap',
                                { 'fw-bold': highlighted }
                            )} tag="h6" style={{ ...style }}>
                                <a className={classNames('label-detail-card px-0 mx-0', {
                                    'fw-bold': highlighted
                                })}
                                    style={{ fontSize: '14px', ...style }}
                                    title= {name}>
                                     {name}
                                </a>
                            </Flex>
                        </Row>
                        <Row className=" justify-content-end text-end mx-2 g-0 flex-nowrap justify-content-between" style={{ width: '50%', minWidth: '120px' }} >

                            <div className='mx-2 text-start' style={{ width: '60px' }}>
                                <Badge pill bg="transparent" className="text-primary" style={{ width: '60px' }}>
                                    {tiempoFormateado} hr(s)
                                </Badge>
                            </div>
                            <div className="justify-content-end d-flex flex-nowrap" style={{ width: '60%' }}>
                                <Badge pill bg="200">
                                    <div className="m-0 fs-10 fw-semibold text-primary">{percentage.toFixed(0)}%</div>
                                </Badge>
                            </div>
                        </Row>
                    </Flex>
                </Col >
            </Row >
        </div >
    );
};

// ✅ COMPONENTE PRINCIPAL CON TÍTULO ACTUALIZADO
const EstadiasTotal = ({ data, radius, resumenUnidad,onSelectAlertType,title }) => {
    const { getThemeColor } = useAppContext();
        const chartRef = React.useRef(null);
    
    console.log("[EstadiasTotal] Props recibidas A/E:", { 
        dataLength: data?.length, 
        resumenUnidad,
        radius
    });
    
    // ✅ USAR RESUMEN DE UNIDAD CON ORDEN A/E
    const processedData = processData(resumenUnidad);
    const totalHours = processedData.reduce((acc, val) => acc + val.segundos, 0);

    const hrs = Math.floor(totalHours / 3600);
    const mins = Math.floor((totalHours % 3600) / 60);
    const secs = totalHours % 60;

    const padded = (n) => String(n).padStart(2, '0');

    let horasTotales = `${padded(hrs)}:${padded(mins)}:${padded(secs)}`;

    console.log("[EstadiasTotal] Datos procesados A/E:", {
        processedData,
        totalHours
    });

    const handleChartClick = (params) => {
        if (params.data && params.data.alertType) {
            onSelectAlertType ? onSelectAlertType(params.data.alertType) :null; // 🔹 Llamar a la función al hacer click en un segmento
        }
    };
    
    return (
        <Card className="h-md-100">
            
            <FalconCardHeader
                title ={'Estado del Motor (A/E)'}
                light
                titleTag="h6"
            >

            </FalconCardHeader>

            <Card.Body className='py-0'>
                <Row className="justify-content-between g-0 py-3">
                    <Col xs={9}>
                     {
                        /* ✅ Título actualizado */}
                        {/* <h6 className="mt-1">Estado del Motor (A/E)</h6>   */}
                       
                        {processedData.map((item, index) => (
                            <EstadiasTotalItem
                                item={item}
                                index={index}
                                key={index}
                                total={totalHours}
                                totalItems={processedData.length}
                            />
                        ))}
                        
                        {/* ✅ INFORMACIÓN ADICIONAL */}
                        {/* {resumenUnidad && (
                            <div className="mt-3 pt-2 border-top">
                                <div className="text-small-estadias">
                                    <div className="d-flex justify-content-between mb-1">
                                        <span>Distancia Total:</span>
                                        <strong>{(resumenUnidad.distancia_total || 0).toFixed(2)} km</strong>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Velocidad Máxima:</span>
                                        <strong>{resumenUnidad.velocidad_maxima || 0} km/h</strong>
                                    </div>
                                </div>
                            </div>
                        )} */}
                    </Col>
                    <Col xs={3}>
        <div className="position-relative" >

            <ReactEchart
                ref={chartRef}
                echarts={echarts}
                option={getOptionsPie(processedData.map(w=>{
                    return ({...w,totalHours});
                }), getThemeColor)}
                style={{ height: '7.5rem' }}
                onEvents={{ click: handleChartClick }} // 🔹 Detectar clic en gráfico
            />

            <div className="position-absolute top-50 start-50 translate-middle text-center">
                <span className="fs-6 m-0 font-sans-serif fw-medium">A/E</span>
            </div>
                            {/*<BasicECharts
                                echarts={echarts}
                                options={getOptions(getThemeColor, processedData, radius)}
                                style={{ width: '6.625rem', height: '6.625rem' }}
                            />*/}
                        </div>
                    </Col>
                </Row>
            </Card.Body>
                {processedData.length === 1 ? <></>:<Row className='px-1 pb-2 justify-content-end border-top border-200 pt-2'>
                    <Col md={12} className='text-end '>
                        <div className="fw-bold label-detail-card pe-3"> Total: {horasTotales} hr(s)</div>
                    </Col>
                </Row>}
        </Card>
    );
};

// El resto del código permanece igual...
export default EstadiasTotal;


//--------------------------------------------------
// import React from 'react';
// import PropTypes from 'prop-types';
// import { Card, Col, Row } from 'react-bootstrap';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import Flex from '../../../../../components/common/Flex';
// import { PieChart } from 'echarts/charts';
// import * as echarts from 'echarts/core';
// import BasicECharts from '../../../../../components/common/BasicEChart';
// import {
//     GridComponent,
//     TitleComponent,
//     TooltipComponent
// } from 'echarts/components';
// import { CanvasRenderer } from 'echarts/renderers';
// import { useAppContext } from '../../../../../providers/AppProvider';
// import { segundosATiempo } from '../../../../../helpers/utils';
// import { formatearTiempo, obtenerTiempoMotor } from '../../../../../utils/useReporteCalculations';

// echarts.use([
//     TitleComponent,
//     TooltipComponent,
//     GridComponent,
//     PieChart,
//     CanvasRenderer
// ]);

// // ✅ PROCESAR DATOS USANDO RESUMEN DE UNIDAD
// const processData = (resumenUnidad) => {
//     console.log("[EstadiasTotal processData] Resumen recibido:", resumenUnidad);
    
//     if (!resumenUnidad) {
//         return [
//             { name: 'Motor Encendido', color: '#28a745', segundos: 0 },
//             { name: 'Motor Apagado', color: '#dc3545', segundos: 0 }
//         ];
//     }

//     const groupedData = [
//         { 
//             name: 'Motor Encendido', 
//             color: '#28a745', 
//             segundos: resumenUnidad.tiempo_actividad || 0
//         },
//         { 
//             name: 'Motor Apagado', 
//             color: '#dc3545', 
//             segundos: resumenUnidad.tiempo_apagado || 0
//         }
//     ];

//     console.log("[EstadiasTotal processData] Datos procesados:", groupedData);
//     return groupedData;
// };

// // ✅ CONFIGURACIÓN DE ECHARTS CON VALIDACIÓN
// const getOptions = (getThemeColor, data, radius) => {
//     const totalSegundos = data.reduce((acc, item) => acc + item.segundos, 0);
    
//     console.log("[EstadiasTotal getOptions] Total segundos:", totalSegundos);
    
//     // Si no hay datos, mostrar gráfico vacío
//     if (totalSegundos === 0) {
//         return {
//             series: [{
//                 name: 'E/A',
//                 type: 'pie',
//                 radius,
//                 data: [{ value: 1, name: 'Sin datos', itemStyle: { color: '#e9ecef' } }],
//                 label: {
//                     show: true,
//                     position: 'center',
//                     formatter: 'Sin\ndatos',
//                     fontSize: 16,
//                     color: getThemeColor('gray-600')
//                 }
//             }]
//         };
//     }

//     return {
//         color: data.map(d => d.color),
//         tooltip: {
//             padding: [7, 10],
//             transitionDuration: 0,
//             backgroundColor: getThemeColor('gray-100'),
//             borderColor: getThemeColor('gray-300'),
//             textStyle: { color: getThemeColor('gray-1100') },
//             borderWidth: 1,
//             formatter: params => {
//                 const porcentaje = totalSegundos > 0 ? ((params.value / totalSegundos) * 100).toFixed(1) : 0;
//                 const tiempo = formatearTiempo(params.value);
//                 return `<strong>${params.data.name}:</strong><br/>${tiempo} hrs (${porcentaje}%)`;
//             }
//         },
//         series: [{
//             name: 'E/A',
//             type: 'pie',
//             radius,
//             avoidLabelOverlap: false,
//             emphasis: {
//                 scale: false
//             },
//             itemStyle: {
//                 borderWidth: 2,
//                 borderColor: getThemeColor('gray-100')
//             },
//             label: {
//                 show: true,
//                 position: 'center',
//                 formatter: '{a}',
//                 fontSize: 23,
//                 color: getThemeColor('gray-1100')
//             },
//             labelLine: {
//                 show: false
//             },
//             data: data.map(d => ({
//                 value: d.segundos,
//                 name: d.name,
//                 itemStyle: { color: d.color }
//             }))
//         }]
//     };
// };

// // ✅ COMPONENTE PARA MOSTRAR CADA ITEM CON VALIDACIÓN
// const EstadiasTotalItem = ({ item, index, total }) => {
//     const { name, color, segundos } = item;
    
//     // Calcular porcentaje de manera segura
//     const percentage = total > 0 ? ((segundos * 100) / total) : 0;
    
//     // Formatear tiempo usando la función genérica
//     const tiempoFormateado = formatearTiempo(segundos);
    
//     return (
//         <Flex
//             alignItems="center"
//             justifyContent="between"
//             className={`fw-semibold fs-11 ${index === 0 && 'mt-3'}`}
//         >
//             <p className="mb-1">
//                 <FontAwesomeIcon
//                     icon="circle"
//                     className="me-2"
//                     style={{ color: color }}
//                 />
//                 {name}
//             </p>
//             <span 
//                 className="mb-1" 
//                 style={{ display: 'block', width: '40%', textAlign: 'center' }}
//             >
//                 {tiempoFormateado} hrs
//             </span>
//             <div className="d-xxl-none">
//                 {percentage.toFixed(0)}%
//             </div>
//         </Flex>
//     );
// };

// // ✅ COMPONENTE PRINCIPAL CON DATOS DE RESUMEN
// const EstadiasTotal = ({ data, radius, resumenUnidad }) => {
//     const { getThemeColor } = useAppContext();
    
//     console.log("[EstadiasTotal] Props recibidas:", { 
//         dataLength: data?.length, 
//         resumenUnidad,
//         radius
//     });
    
//     // ✅ USAR RESUMEN DE UNIDAD EN LUGAR DE DATA
//     const processedData = processData(resumenUnidad);
//     const totalHours = processedData.reduce((acc, val) => acc + val.segundos, 0);

//     console.log("[EstadiasTotal] Datos procesados:", {
//         processedData,
//         totalHours
//     });

//     return (
//         <Card className="h-md-100">
//             <Card.Body>
//                 <Row className="justify-content-between g-0">
//                     <Col xs={5} sm={6} xxl className="pe-2">
//                         <h6 className="mt-1">Estado del Motor</h6>
//                         {processedData.map((item, index) => (
//                             <EstadiasTotalItem
//                                 item={item}
//                                 index={index}
//                                 key={index}
//                                 total={totalHours}
//                             />
//                         ))}
                        
//                         {/* ✅ INFORMACIÓN ADICIONAL */}
//                         {resumenUnidad && (
//                             <div className="mt-3 pt-2 border-top">
//                                 <div className="small text-muted">
//                                     <div className="d-flex justify-content-between mb-1">
//                                         <span>Distancia Total:</span>
//                                         <strong>{(resumenUnidad.distancia_total || 0).toFixed(2)} km</strong>
//                                     </div>
//                                     <div className="d-flex justify-content-between">
//                                         <span>Velocidad Máxima:</span>
//                                         <strong>{resumenUnidad.velocidad_maxima || 0} km/h</strong>
//                                     </div>
//                                 </div>
//                             </div>
//                         )}
//                     </Col>
//                     <Col xs="auto">
//                         <div className="ps-0">
//                             <BasicECharts
//                                 echarts={echarts}
//                                 options={getOptions(getThemeColor, processedData, radius)}
//                                 style={{ width: '6.625rem', height: '6.625rem' }}
//                             />
//                         </div>
//                     </Col>
//                 </Row>
//             </Card.Body>
//         </Card>
//     );
// };

// // ✅ PROP TYPES ACTUALIZADOS
// EstadiasTotalItem.propTypes = {
//     item: PropTypes.shape({
//         name: PropTypes.string.isRequired,
//         color: PropTypes.string.isRequired,
//         segundos: PropTypes.number.isRequired
//     }),
//     index: PropTypes.number.isRequired,
//     total: PropTypes.number.isRequired
// };

// EstadiasTotal.propTypes = {
//     data: PropTypes.array,
//     radius: PropTypes.array.isRequired,
//     resumenUnidad: PropTypes.object // ✅ NUEVA PROP
// };

// EstadiasTotal.defaultProps = {
//     data: [],
//     resumenUnidad: null
// };

// export default EstadiasTotal;

//---------------------------------------------
// import React from 'react';
// import { Card } from 'react-bootstrap';
// import { formatearTiempo, obtenerTiempoMotor } from '../../../../../utils/useReporteCalculations';

// const EstadiasTotal = ({ data, radius, resumenUnidad }) => {
//   console.log("[EstadiasTotal] Datos recibidos:", { data: data?.length, resumenUnidad });

//   // ✅ USAR RESUMEN DE UNIDAD SI ESTÁ DISPONIBLE
//   const tiempoEncendido = resumenUnidad ? 
//     obtenerTiempoMotor(resumenUnidad, 'encendido') : 
//     "00:00:00 hrs";
    
//   const tiempoApagado = resumenUnidad ? 
//     obtenerTiempoMotor(resumenUnidad, 'apagado') : 
//     "00:00:00 hrs";

//   // ✅ CALCULAR ESTADÍSTICAS DE ESTADÍAS
//   const estadisticasEstadias = data && data.length > 0 ? (() => {
//     const tiemposPorCategoria = {
//       clientes: 0,
//       geocercas: 0,
//       referencias: 0,
//       indeterminado: 0
//     };

//     let tiempoTotal = 0;

//     data.forEach(estadia => {
//       const tiempoSegundos = parseFloat(estadia.tiempo_segundos) || 0;
//       tiempoTotal += tiempoSegundos;
      
//       const categoria = (estadia.tipo || 'indeterminado').toLowerCase();
      
//       if (categoria.includes('cliente')) {
//         tiemposPorCategoria.clientes += tiempoSegundos;
//       } else if (categoria.includes('geocerca')) {
//         tiemposPorCategoria.geocercas += tiempoSegundos;
//       } else if (categoria.includes('referencia')) {
//         tiemposPorCategoria.referencias += tiempoSegundos;
//       } else {
//         tiemposPorCategoria.indeterminado += tiempoSegundos;
//       }
//     });

//     return {
//       total: tiempoTotal,
//       clientes: tiempoTotal > 0 ? Math.round((tiemposPorCategoria.clientes / tiempoTotal) * 100) : 0,
//       geocercas: tiempoTotal > 0 ? Math.round((tiemposPorCategoria.geocercas / tiempoTotal) * 100) : 0,
//       referencias: tiempoTotal > 0 ? Math.round((tiemposPorCategoria.referencias / tiempoTotal) * 100) : 0,
//       indeterminado: tiempoTotal > 0 ? Math.round((tiemposPorCategoria.indeterminado / tiempoTotal) * 100) : 0
//     };
//   })() : {
//     clientes: 0,
//     geocercas: 0,
//     referencias: 0,
//     indeterminado: 0,
//     total: 0
//   };

//   console.log("[EstadiasTotal] Estadísticas calculadas:", estadisticasEstadias);

//   return (
//     <Card className="h-100">
//       <Card.Header>
//         <h6 className="mb-0">Estado del Motor</h6>
//       </Card.Header>
//       <Card.Body>
//         {/* ✅ SECCIÓN DE ESTADO DEL MOTOR */}
//         <div className="row">
//           <div className="col-6 text-center">
//             <div className="mb-2">
//               <i className="fas fa-play-circle text-success" style={{ fontSize: '2rem' }}></i>
//             </div>
//             <h6 className="text-success">Motor Encendido</h6>
//             <h4 className="mb-0">{tiempoEncendido}</h4>
//           </div>
//           <div className="col-6 text-center">
//             <div className="mb-2">
//               <i className="fas fa-stop-circle text-danger" style={{ fontSize: '2rem' }}></i>
//             </div>
//             <h6 className="text-danger">Motor Apagado</h6>
//             <h4 className="mb-0">{tiempoApagado}</h4>
//           </div>
//         </div>

//         <hr />

//         {/* ✅ SECCIÓN DE PORCENTAJES DE UBICACIÓN */}
//         <h6 className="mb-3">Tiempo de estadía por ubicación</h6>
        
//         {estadisticasEstadias.total === 0 ? (
//           <div className="text-center text-muted">
//             <p>No hay estadías para mostrar</p>
//           </div>
//         ) : (
//           <div>
//             <div className="d-flex justify-content-between mb-2">
//               <span><i className="fas fa-users text-success me-2"></i>Clientes</span>
//               <strong className="text-success">{estadisticasEstadias.clientes}%</strong>
//             </div>
//             <div className="d-flex justify-content-between mb-2">
//               <span><i className="fas fa-map-marked-alt text-primary me-2"></i>Geocercas</span>
//               <strong className="text-primary">{estadisticasEstadias.geocercas}%</strong>
//             </div>
//             <div className="d-flex justify-content-between mb-2">
//               <span><i className="fas fa-map-pin text-warning me-2"></i>Referencias</span>
//               <strong className="text-warning">{estadisticasEstadias.referencias}%</strong>
//             </div>
//             <div className="d-flex justify-content-between mb-2">
//               <span><i className="fas fa-question-circle text-secondary me-2"></i>Indeterminado</span>
//               <strong className="text-secondary">{estadisticasEstadias.indeterminado}%</strong>
//             </div>
//           </div>
//         )}

//         {/* ✅ INFORMACIÓN ADICIONAL SI ESTÁ DISPONIBLE */}
//         {resumenUnidad && (
//           <>
//             <hr />
//             <div className="row small text-muted">
//               <div className="col-6 text-center">
//                 <div>Distancia Total</div>
//                 <strong>{(resumenUnidad.distancia_total || 0).toFixed(2)} km</strong>
//               </div>
//               <div className="col-6 text-center">
//                 <div>Velocidad Máxima</div>
//                 <strong>{resumenUnidad.velocidad_maxima || 0} km/h</strong>
//               </div>
//             </div>
//           </>
//         )}
//       </Card.Body>
//     </Card>
//   );
// };

// export default EstadiasTotal;


// import React from 'react';
// import PropTypes from 'prop-types';
// import { Card, Col, Row } from 'react-bootstrap';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import Flex from '../../../../../components/common/Flex';
// import { PieChart } from 'echarts/charts';
// import * as echarts from 'echarts/core';
// import BasicECharts from '../../../../../components/common/BasicEChart';
// import {
//     GridComponent,
//     TitleComponent,
//     TooltipComponent
// } from 'echarts/components';
// import { CanvasRenderer } from 'echarts/renderers';
// import { useAppContext } from '../../../../../providers/AppProvider';
// import { segundosATiempo } from '../../../../../helpers/utils';
// segundosATiempo

// echarts.use([
//     TitleComponent,
//     TooltipComponent,
//     GridComponent,
//     PieChart,
//     CanvasRenderer
// ]);

// const processData = (data) => {
//     const filteredData = data //.filter(entry => [0, 1, 2].includes(entry.tipoReporteEstadia));

//     const groupedData = {
//         encendido: { name: 'Motor Encendido', color: 'green', segundos: 0, meters: 0 },
//         apagado: { name: 'Motor Apagado', color: 'red', segundos: 0, meters: 0 }
//     };

//     filteredData.forEach(entry => {
//         const tiempoHoras = entry.tipo_motor_inicio === 1 ? entry.tiempo_encendido : entry.tiempo_apagado //entry.velocidad > 0 ? (entry.distancia / entry.velocidad) : 0;
//         const key = entry.tipo_motor_inicio === 1 ? 'encendido' : 'apagado';

//         groupedData[key].segundos += tiempoHoras;
//         //groupedData[key].meters += entry.distancia;
//     });

//     return Object.values(groupedData).map(group => ({
//         ...group,
//         segundos: group.segundos,
//         meters: group.meters
//     }));
// };

// const getOptions = (getThemeColor, data, radius, totalMeters) => ({
//     color: data.map(d => d.color), // Usa los colores correctos directamente

//     tooltip: {
//         padding: [7, 10],
//         transitionDuration: 0,
//         backgroundColor: getThemeColor('gray-100'),
//         borderColor: getThemeColor('gray-300'),
//         textStyle: { color: getThemeColor('gray-1100') },
//         borderWidth: 1,
//         formatter: params => `<strong>${params.data.name}:</strong> ${params.percent}%`
//     },
//     series: [
//         {
//             //name: `${totalMeters}M`,
//             name: `E/A`,
//             type: 'pie',
//             radius,
//             avoidLabelOverlap: false,
//             emphasis: {
//                 scale: false
//             },
//             itemStyle: {
//                 borderWidth: 2,
//                 borderColor: getThemeColor('gray-100')
//             },
//             label: {
//                 show: true,
//                 position: 'center',
//                 formatter: `{a}`,
//                 fontSize: 23,
//                 color: getThemeColor('gray-1100')
//             },
//             labelLine: {
//                 show: false // 🔹 Evita líneas que puedan interferir
//             },
//             data: data.map(d => ({
//                 value: d.segundos, // 🔹 Asegura que value sea el total de horas
//                 name: d.name,  // 🔹 El nombre del grupo
//                 itemStyle: { color: d.color } // 🔹 Aplica el color correcto
//             }))
//         }
//     ]
// });


// const EstadiasTotalItem = ({ item, index, total }) => {
//     const { name, color, segundos } = item;
//     const percentage = 0//((segundos * 100) / total);
//     return (
//         <Flex
//             alignItems="center"
//             justifyContent="between"
//             className={`fw-semibold fs-11 ${index === 0 && 'mt-3'}`}
//         >
//             <p className="mb-1">
//                 <FontAwesomeIcon
//                     icon="circle"
//                     className={`me-2`}
//                     style={{ color: `${color}` }}
//                 />
//                 {name}
//             </p>
//             <span className="mb-1" style={{ display: 'block', width: '40%', textAlign: 'center' }}>{segundosATiempo(segundos)} hrs</span>
//             <div className="d-xxl-none">{percentage}%</div>
//         </Flex>
//     );
// };

// const EstadiasTotal = ({ data, radius }) => {
//     const { getThemeColor } = useAppContext();
//     const processedData = processData(data);
//     const totalHours = processedData.reduce((acc, val) => val.segundos + acc, 0);
//     const totalMeters = processedData.reduce((acc, val) => val.meters + acc, 0);

//     return (
//         <Card className="h-md-100">
//             <Card.Body>
//                 <Row className="justify-content-between g-0">
//                     <Col xs={4} sm={5} xxl className="pe-2">
//                         <h6 className="mt-1">Estado del Motor</h6>
//                         {processedData.map((item, index) => (
//                             <EstadiasTotalItem
//                                 item={item}
//                                 index={index}
//                                 key={index}
//                                 total={totalHours}
//                             />
//                         ))}
//                     </Col>
//                     <Col xs="auto">
//                         <div className="ps-0">
//                             <BasicECharts
//                                 echarts={echarts}
//                                 options={getOptions(getThemeColor, processedData, radius, totalMeters)}
//                                 style={{ width: '6.625rem', height: '6.625rem' }}
//                             />
//                         </div>
//                     </Col>
//                 </Row>
//             </Card.Body>
//         </Card>
//     );
// };

// EstadiasTotalItem.propTypes = {
//     item: PropTypes.shape({
//         name: PropTypes.string.isRequired,
//         color: PropTypes.string.isRequired,
//         segundos: PropTypes.number.isRequired
//     }),
//     index: PropTypes.number.isRequired,
//     total: PropTypes.number.isRequired
// };

// EstadiasTotal.propTypes = {
//     data: PropTypes.array.isRequired,
//     radius: PropTypes.array.isRequired
// };

// export default EstadiasTotal;
